// Gate B is platform-specific. An unrecognized browser stays half duplex;
// the personal preference alone never opens the mic during playback.
import { nativePlatform } from '@/lib/shell';
import type { VoiceConfig, VoiceDuplexPlatform } from './contracts';

type BrowserBrands = {
  mobile?: boolean;
  brands?: readonly { brand: string }[];
};

const isPlatform = (value: unknown): value is VoiceDuplexPlatform =>
  value === 'desktop-chrome' || value === 'ios-shell' || value === 'android-shell';

/** Missing or malformed deployment proof never grants duplex capture. */
export function parseFullDuplex(value: unknown): VoiceConfig['fullDuplex'] {
  const off = { available: false, reason: 'not_configured', platforms: [] };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return off;
  const block = value as Record<string, unknown>;
  if (
    typeof block.available !== 'boolean' ||
    !(
      block.reason === null ||
      (typeof block.reason === 'string' && block.reason.length > 0 && block.reason.length <= 64)
    ) ||
    !Array.isArray(block.platforms) ||
    !block.platforms.every(isPlatform)
  )
    return off;
  return {
    available: block.available && block.platforms.length > 0,
    reason: typeof block.reason === 'string' ? block.reason : null,
    platforms: [...block.platforms],
  };
}

/** Structural platform hints only; no speech or reply text is inspected. */
export function voiceDuplexPlatform(
  native: string | null = nativePlatform(),
  browser: BrowserBrands | undefined = (
    globalThis.navigator as Navigator & { userAgentData?: BrowserBrands }
  )?.userAgentData,
): VoiceDuplexPlatform | null {
  if (native !== null) {
    if (native === 'ios') return 'ios-shell';
    if (native === 'android') return 'android-shell';
    return null;
  }
  return browser?.mobile === false && browser.brands?.some(({ brand }) => brand === 'Google Chrome')
    ? 'desktop-chrome'
    : null;
}

export function fullDuplexAvailable(
  voice: Pick<VoiceConfig, 'fullDuplex'> | null | undefined,
  platform: VoiceDuplexPlatform | null = voiceDuplexPlatform(),
): boolean {
  return Boolean(
    platform && voice?.fullDuplex?.available && voice.fullDuplex.platforms.includes(platform),
  );
}
